/** @jsx jsx */
import React, { createRef, Fragment, useContext, useState } from 'react'
import { Box, Flex, jsx, Text } from 'theme-ui'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import styled from '@emotion/styled'

import {
  mobile,
  HideOnSmDesktop,
  HideOnSmDesktopUp,
  Space,
  HideOnTabletDown,
} from '..'
import { useWindowSize } from '../../../hooks/useWindowSize'
import Link from '../Typography/Link/Link'
import Body from '../Typography/Body/Body'

import { css, Global } from '@emotion/core'
import theme from '../../../gatsby-plugin-theme-ui'
import PromotionalAlert from '../PromotionalAlert/PromotionalAlert'
import SubPromotionalAlert from '../PromotionalAlert/SubPromotionalAlert'

import { BgImg } from '../BgImage'
import { ShopifyContext } from '../../Layout'
import { GorgiasButton } from '../../footer/ColumnLink'
import Cart from '../../../icons/cart.svg'

import chevronLeft from '../../../icons/chevronLeft.svg'
import Plus from '../../../icons/Plus.svg'
import Minus from '../../../icons/minus.svg'

import bars from '../../../icons/bars.svg'

import {
  checkPathnameMatchWithWildcard,
  getItemIndexByProperty,
} from '../../../utils'
import ScrollMenu from 'react-horizontal-scrolling-menu'
import { useLocation } from 'react-use'
import useDocumentPosition from '../../../hooks/useDocumentPosition'

export const Overlay = styled.div`
  &::after {
    content: '';
    background: black;
    height: 100%;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${p => p.theme.zIndex[4]};
    transition: 0.2s all;
    opacity: ${props => (props.isOpen ? '.8' : '0')};
    visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
    top: 0;
  }
`

const Logo = styled.img``

const FixedNav = styled.div`
  position: ${p => (p.sticky ? 'fixed' : 'absolute')};
  top: 0;
  width: 100%;
  z-index: ${p => p.theme.zIndex[4] + 2};
`

const NavWrapper = styled.div`
  background: white;
  height: ${p => p.theme.sizes.navHeight}px;
  z-index: ${p => p.theme.zIndex[4] + 2};
  box-shadow: ${p => p.theme.shadows.navBottomShadow};
  ${mobile(css`
    height: ${theme.sizes.mobileNavHeight}px;
  `)}
`

const NavSubMenu = styled.div`
  background: ${p => p.theme.colors.black};
  height: ${p => p.theme.sizes.navSubMenuHeight}px;
  z-index: ${p => p.theme.zIndex[4] + 2};
  box-shadow: ${p => p.theme.shadows.navBottomShadow};
  width: 100vw;
  color: ${p => p.theme.colors.white};
`

const ExtendedMenuDropdown = styled.div`
  height: ${p => (p.isOpen ? 'auto' : '0px')};
  overflow: hidden;
  transition: height 0.3s ease-in-out 0s;
  z-index: ${p => p.theme.zIndex[5] + 4};
  position: relative;
`

const DrawerMenuWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  transform: ${p => (p.isOpen ? 'translateX(0%)' : 'translateX(-100%)')};
  width: ${p => (p.isOpen ? '100%' : '0px')};
  overflow: ${p => (p.isOpen ? 'auto' : 'hidden')};
  transition: all 300ms;
  background: white;
  max-width: 500px;
  z-index: ${p => p.theme.zIndex[4] + 2};
`

const ExtendedMenuContainer = styled(Flex)`
  display: flex;
  background: white;
  width: 100%;
  height: 100%;
  flex-direction: row;
  box-shadow: ${p => p.theme.shadows.nav};
  border: ${p => p.theme.borders.default};
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
`

export const NavSpacer = ({
  subNavFixed,
  showMobileSubNav,
  hasSubPromoAlert,
  customHeight,
}) => {
  const subNavHeight =
    showMobileSubNav && !subNavFixed ? theme.sizes.navSubMenuHeight : 0

  const subPromoAlertHeight = hasSubPromoAlert
    ? theme.sizes.promotionalAlertHeight
    : 0

  return (
    <Space
      sx={{
        height: theme =>
          customHeight
            ? customHeight(theme)
            : [
                `${theme.sizes.mobileNavHeight +
                  subNavHeight +
                  subPromoAlertHeight +
                  theme.sizes.promotionalAlertHeight}px`,
                `${theme.sizes.mobileNavHeight +
                  subNavHeight +
                  subPromoAlertHeight +
                  theme.sizes.promotionalAlertHeight}px`,
                `${theme.sizes.navHeight +
                  subPromoAlertHeight +
                  theme.sizes.promotionalAlertHeight}px`,
              ],
      }}
    />
  )
}

export const NavLink = props => (
  <Box sx={{ position: 'relative' }}>
    <Link
      {...props}
      activeClassName="active"
      sx={{
        color: 'inherit',
        '&.active': {
          color: 'primary',
        },
      }}
    />
    {props.isActive && (
      <div
        sx={{
          display: 'block',
          position: 'absolute',
          top: '121%',
          height: '2px',
          width: '100%',
          backgroundColor: 'text',
          transformOrigin: ' center top',
          transform: 'scale(0, 1)',
          transition: 'color 0.1s, transform 0.2s ease-out',
          transformOrigin: 'center top',
          transform: 'scale(1, 1)',
        }}
      />
    )}
  </Box>
)

export const LINK_COLUMN_RECURISON = graphql`
  fragment ContentfulLinkColumnRecursionFragment on ContentfulLinkColumn {
    ...ContentfulLinkColumnFragment
    links {
      ...GetNavigationLinkFragment
      ... on ContentfulLinkColumn {
        ...ContentfulLinkColumnFragment
        links {
          ...GetNavigationLinkFragment
          ... on ContentfulLinkColumn {
            ...ContentfulLinkColumnFragment
            links {
              ...GetNavigationLinkFragment
              ... on ContentfulLinkColumn {
                ...ContentfulLinkColumnFragment
                links {
                  ...GetNavigationLinkFragment
                  ... on ContentfulLinkColumn {
                    ...ContentfulLinkColumnFragment
                    links {
                      ...GetNavigationLinkFragment
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const GET_NAV_LINKS = graphql`
  fragment GetNavigationLinkFragment on ContentfulLinkColumnContentfulNavigationLinkUnion {
    ... on ContentfulNavigationLink {
      navLinkText
      navLinkSlug
      navLinkImage {
        fluid(maxWidth: 300) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      bold
      color
      highlightText
      highlightTextColor
      extendedCta {
        text {
          raw
        }
        title
        image {
          fluid(maxWidth: 300) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
    }
  }
`

export const GET_LINK_COLUMN = graphql`
  fragment ContentfulLinkColumnFragment on ContentfulLinkColumn {
    __typename
    header
    hideHeader
    toSlug
    mobileHeaderBold
    mobileHeaderColor
    hideOnDevice
    headerImage {
      fluid(maxWidth: 300) {
        ...GatsbyContentfulFluid_withWebp_noBase64
      }
    }
  }
`

export const GET_NAV = graphql`
  fragment ContentfulNavBarDetails on ContentfulNavBar {
    hideMobileSubNavSlugs
    brandLogo {
      file {
        url
      }
    }
    brandLogoToSlug
    defaultNav
    linkColumns {
      ...ContentfulLinkColumnRecursionFragment
    }
    mobileCtaLink {
      navLinkText
      navLinkSlug
      bold
      color
      extendedCta {
        image {
          fluid(maxWidth: 300) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
    }
  }
`
export const pinBody = css`
  html,
  body {
    overflow-y: hidden;
    padding-right: 7.5px;
  }
`
const Nav = ({ data: navBar, subNavLinks, subNav }) => {
  const subPromoAlert = useStaticQuery(GET_SUBNAV_ALERT)
  const { setCartOpen, cartOpen, cartHasItems } = useContext(ShopifyContext)
  const { pathname } = useLocation()
  const [open, setOpenMenu] = useState(false)
  const { mobile, desktop, smallScreen } = useWindowSize()

  // Sticky nav Ref
  const navRef = createRef()

  const { fixedPosition } = useDocumentPosition(navRef)

  function toggleMenu({ override }) {
    setOpenMenu(override !== undefined ? override : !open)
  }

  const showMobileSubNav =
    navBar &&
    smallScreen &&
    (subNavLinks ||
      (navBar.hideMobileSubNavSlugs &&
        !navBar.hideMobileSubNavSlugs.find(i =>
          checkPathnameMatchWithWildcard(pathname, i)
        )))

  const subNavFixed = fixedPosition && smallScreen
  const hideSubPromoAlert = fixedPosition
  const hasSubPromoAlert =
    subPromoAlert && subPromoAlert.contentfulPromotionalAlert
  return [
    <FixedNav sticky={!!(subNavFixed || !smallScreen)}>
      <PromotionalAlert hide={hideSubPromoAlert || (mobile && open)} />
      {open && smallScreen && <Global styles={pinBody} />}
      <NavWrapper
        ref={navRef}
        onMouseLeave={() => !smallScreen && toggleMenu({ override: false })}
      >
        <header
          sx={{
            margin: 'auto',
            display: 'grid',
            gridGap: 3,
            maxWidth: 'pageMaxWidth',
            mx: 'auto',
            px: [12, 20, 20, 20, 20, 70],
            gridAutoFlow: 'row',
            gridTemplateColumns: [
              '2fr 4fr 2fr',
              '2fr 4fr 2fr',
              '2fr 4fr 2fr',
              '2fr 4fr 2fr',
              '1fr 4fr 1fr',
            ],
            variant: 'styles.header',
            zIndex: 2,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
          }}
        >
          {/* <HideOnSmDesktopUp>
            <NavLink
              to="/faq"
              sx={{
                paddingX: [0, 0, 0],
                position: 'relative',
              }}
            >
              Help
        
            </NavLink>
          </HideOnSmDesktopUp> */}
          <HideOnSmDesktopUp>
            <Logo onClick={toggleMenu} sx={{ width: '24px' }} src={bars} />
          </HideOnSmDesktopUp>
          <Flex
            sx={{
              alignItems: 'stretch',
              justifyContent: ['center', null, null, null, 'flex-start'],
              margin: [0, null, null, null, 0],
            }}
          >
            <NavLink to={navBar.brandLogoToSlug || '/'} title="Home">
              <img
                sx={{
                  width: ['158px'],
                  variant: 'styles.navlink',
                  p: 0,
                  px: 0,
                }}
                src={navBar.brandLogo.file.url}
                alt="jaxxon"
              />
              <span
                sx={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  top: -9999,
                }}
              >
                Home
              </span>
            </NavLink>
          </Flex>
          <HideOnTabletDown>
            <NavLinks
              activeLink={open}
              setOpenMenu={selected => toggleMenu({ override: selected })}
              links={navBar.linkColumns.filter(
                lc => lc.hideOnDevice !== 'desktop'
              )}
              setMenuClosed={() => !mobile && toggleMenu({ override: false })}
            />
          </HideOnTabletDown>

          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              order: 2,
              height: '100%',
            }}
          >
            <HideOnSmDesktop>
              <NavLink
                to="/faq"
                sx={{
                  position: 'relative',
                  variant: 'styles.navlink',
                }}
              >
                Help
                {/* <Logo
                  sx={{
                    width: ['23px'],
                    height: ['23px'],
                    mr: [3, 3, 4],
                  }}
                  src={questionCircle}
                ></Logo> */}
              </NavLink>
            </HideOnSmDesktop>

            <NavLink
              sx={{
                paddingX: [0, 0, 0],
                position: 'relative',
                ml: 10,
              }}
              onClick={() => setCartOpen && setCartOpen(!cartOpen)}
            >
              <Box as="span">
                <Logo
                  sx={{ width: '26px', height: '26px' }}
                  src={Cart}
                  alt="cart"
                />

                {!cartOpen && cartHasItems > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-3px',
                      right: [-1, -1, -1],
                      bg: 'background',
                      width: 15,
                      height: 15,
                      borderRadius: '100%',
                    }}
                  >
                    <Body variant="body3" sx={{ color: 'tetx', ml: '3px' }}>
                      {cartHasItems}
                    </Body>
                  </Box>
                )}
              </Box>
            </NavLink>
          </Flex>
        </header>
        <HideOnSmDesktop>
          {/* <SubPromotionalAlert hide={subNavFixed} /> */}

          {desktop && (
            <ExtendedMenu
              setOpenMenu={toggleMenu}
              isDrawer={mobile}
              openedColumn={open}
            />
          )}
        </HideOnSmDesktop>
        <HideOnSmDesktopUp>
          {smallScreen && (
            <>
              <DrawerMenu
                isOpen={open}
                setOpenMenu={toggleMenu}
                isDrawer={smallScreen}
                linkColumns={navBar.linkColumns}
                mobileCtaLink={navBar.mobileCtaLink}
                handleClose={() => toggleMenu({ override: false })}
                {...navBar}
              />
              <Overlay
                onClick={() => toggleMenu({ override: false })}
                isOpen={open}
              />
            </>
          )}
        </HideOnSmDesktopUp>
      </NavWrapper>
      <SubPromotionalAlert subPromoAlert={subPromoAlert} />

      {showMobileSubNav && (
        <MobileNavSubMenu
          navBar={navBar}
          currentLocation={pathname}
          subNavLinks={subNavLinks}
          subNav={subNav}
        />
      )}
    </FixedNav>,
    <NavSpacer
      hasSubPromoAlert={hasSubPromoAlert}
      subNavFixed={subNavFixed}
      showMobileSubNav={showMobileSubNav}
    />,
  ]
}

const MobileNavSubMenu = ({ currentLocation, navBar, subNavLinks, subNav }) => {
  const links = subNav.links || subNavLinks || navBar.linkColumns

  return (
    <NavSubMenu
      css={css`
        .menu-item-wrapper {
          outline: none;
        }
      `}
    >
      <Box
        sx={{
          fontSize: 1,
          height: '100%',
          mt: '9px',
          float: 'left',
          display: 'flex',
        }}
      >
        {subNav.breadCrumbLink && (
          <>
            <Link
              variant="subNavText"
              sx={{
                padding: '0px 16px 0px',
                fontWeight: 'semiBold',
                outline: '0px !important',
              }}
              to={subNav.breadCrumbLink.navLinkSlug}
            >
              {subNav.breadCrumbLink.navLinkText}
            </Link>
            <Text variant="subNavText" sx={{ fontSize: 2 }}>
              /
            </Text>
          </>
        )}
      </Box>
      <ScrollMenu
        alignCenter={!subNav.breadCrumbLink}
        data={links.map((i, k) => {
          const locationSlug = i.toSlug || i.navLinkSlug
          const active = locationSlug === currentLocation.replace('/', '')
          if (!locationSlug) {
            return <Fragment />
          }
          return (
            <Box sx={{ fontSize: 1, height: '100%', mt: '9px' }} key={k}>
              <Link
                variant="subNavText"
                sx={{
                  padding: '0px 16px 0px',
                  fontWeight: active && 'semiBold',
                  outline: '0px !important',
                }}
                to={locationSlug}
              >
                {i.header || i.navLinkText}
              </Link>
            </Box>
          )
        })}
      />
    </NavSubMenu>
  )
}

const MobileDrawerListContainer = ({
  c,
  showBackBtn,
  handleClose,
  goBack,
  lastItem,
  ...rest
}) => {
  // const [nestedMenuOpen, setNestedMenuOpen] = useState(false)

  return [
    <Flex
      sx={{
        pb: 16,
        cursor: 'pointer',
        alignItems: 'center',
        position: 'absolute',
        top: 23,
        left: 15,
      }}
      onClick={handleClose}
    >
      <Logo src={chevronLeft} sx={{ width: '14px', mr: 10 }} />
    </Flex>,
    <>
      <NavMobileList
        content={c}
        lastItem={lastItem}
        handleClose={() => {
          // setNestedMenuOpen(false)
          handleClose()
        }}
        // setNestedMenuOpen={nextDrawer =>
        //   setNestedMenuOpen([{ ...nextDrawer, hideHeader: true }])
        // }
      />
      {/* <DrawerMenu
        nested
        isOpen={nestedMenuOpen}
        linkColumns={nestedMenuOpen}
        goBack={() => setNestedMenuOpen(false)}
        handleClose={handleClose}
        {...rest}
      /> */}
    </>,
  ]
}

const NavLinks = ({
  setOpenMenu,
  setMenuClosed,
  links,
  defaultLink,
  activeLink,
  ...rest
}) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {links.map((i, k) => {
        return (
          <NavLink
            key={k}
            to={i.toSlug || i.links[0].navLinkSlug}
            isActive={i.header === activeLink.header}
            sx={{
              variant: 'styles.navlink',
            }}
            onMouseEnter={() => {
              setMenuClosed()
              if (i.links && i.links.length > 0) {
                setOpenMenu(i)
              }
            }}
          >
            {i.header}
          </NavLink>
        )
      })}
    </Flex>
  )
}

const ExtendedMenu = ({
  openedColumn,
  isDrawer,

  ...rest
}) => {
  if (!openedColumn || isDrawer) {
    return <Fragment />
  }

  const navLinks =
    openedColumn.links &&
    openedColumn.links.filter(i => i.__typename === 'ContentfulNavigationLink')

  const linkColumns =
    openedColumn.links &&
    openedColumn.links.filter(i => i.__typename === 'ContentfulLinkColumn')

  return (
    <ExtendedMenuDropdown isOpen={Boolean(openedColumn)} {...rest}>
      <ExtendedMenuContainer
        sx={{
          p: 16,
        }}
      >
        <Flex sx={{ maxWidth: 1600 }}>
          <Box sx={{ marginX: ['30px', null, null, null, null, '65px'] }}>
            {navLinks &&
              navLinks.map((c, k) =>
                c.headerImage || c.navLinkImage ? (
                  <Box
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      mx: '10px',
                    }}
                    {...rest}
                    onClick={() => {
                      if (c.toSlug || c.navLinkSlug) {
                        navigate(`/${c.toSlug || c.navLinkSlug}`)
                      }
                    }}
                  >
                    <ImageLink
                      sx={{ minWidth: [200, null, null, null, null, 250] }}
                      content={c}
                      withOverlay
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      cursor: 'pointer',
                      width: 'auto',
                      display: linkColumns.length < 1 && 'inline-block',
                      mx: linkColumns.length < 1 && '10px',
                    }}
                    {...rest}
                    onClick={() => {
                      if (c.toSlug || c.navLinkSlug) {
                        navigate(`/${c.toSlug || c.navLinkSlug}`)
                      }
                    }}
                  >
                    <Body sx={{ color: c.color }} variant={'navDropdownHeader'}>
                      {c.header || c.navLinkText}
                    </Body>
                  </Box>
                )
              )}
          </Box>
          {linkColumns &&
            linkColumns.map((c, k) => (
              <Box sx={{ marginX: '45px' }}>
                <NavExtendedMenuContent key={k} content={c} />
              </Box>
            ))}
        </Flex>
      </ExtendedMenuContainer>
    </ExtendedMenuDropdown>
  )
}

const NavExtendedMenuContent = ({ content, active, ...rest }) => {
  return (
    <Box {...rest}>
      <Body variant={'navDropdownHeader'}> {content.header}</Body>
      {content.links.map((i, k) => {
        return (
          <Body
            variant={'navDropdownLink'}
            onClick={() => {
              if (i.toSlug || i.navLinkSlug) {
                navigate(`/${i.toSlug || i.navLinkSlug}`)
              }
            }}
          >
            {i.header || i.navLinkText}
          </Body>
        )
      })}
    </Box>
  )
}

const ImageLink = ({ content, ...props }) => {
  return (
    <Flex
      sx={{
        height: 140,
        my: '5px',
        width: '100%',
        position: 'relative',
        justifyContent: 'center',
        cursor: 'pointer',
        alignItems: 'center',

        '&:hover': {
          opacity: 0.7,
        },
        p: 0,
      }}
      {...props}
    >
      <Text
        sx={{
          zIndex: 3,
          color: 'white',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 'semiBold',
        }}
        variant={'navMobileLink'}
      >
        {content.header || content.navLinkText}
      </Text>
      <BgImg
        fluid={
          content.headerImage
            ? content.headerImage.fluid
            : content.navLinkImage.fluid
        }
        withOverlay
      />
    </Flex>
  )
}

const DrawerMenu = ({
  isOpen,
  setOpenMenu,
  linkColumns,
  nested,
  mobileCtaLink,
  handleClose,
  ...rest
}) => {
  return (
    <DrawerMenuWrapper nested={nested} isOpen={isOpen} {...rest}>
      <Box sx={{ p: 15, maxHeight: '100%' }}>
        <Box
          sx={{
            py: 25,
            pt: 10,
            width: '100%',
            maxHeight: '100%',
            borderBottom: 'default',
          }}
        >
          {isOpen &&
            (nested ? (
              <Body
                variant={'navMobileLinkSmall'}
                sx={{
                  p: 0,
                  px: 0,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  margin: 0,
                  fontWeight: 'semiBold',
                }}
              >
                {isOpen[0].header}
              </Body>
            ) : (
              <NavLink
                to={rest.brandLogoToSlug || '/'}
                title="Home"
                onClick={() => handleClose()}
              >
                <Logo
                  sx={{
                    width: ['100px'],
                    p: 0,
                    px: 0,
                    margin: 'auto',
                  }}
                  src={rest.brandLogo.file.url}
                  alt="jaxxon"
                />
              </NavLink>
            ))}
        </Box>
        <Box
          sx={{
            overflowY: 'auto',

            pointerEvents: 'auto',
          }}
        >
          {linkColumns &&
            linkColumns.map((c, k) => {
              return (
                <MobileDrawerListContainer
                  lastItem={k === linkColumns.length - 1}
                  c={c}
                  handleClose={handleClose}
                  goBack={() => handleClose()}
                  {...rest}
                />
              )
            })}
          {/* {!nested && (
            <GorgiasButton
              render={({ setShowGorgias }) => (
                <Button
                  onClick={() => setShowGorgias()}
                  variant="black"
                  sx={{ width: '100%', mt: '5px' }}
                >
                  Get Support
                </Button>
              )}
            />
          )} */}
        </Box>
      </Box>
    </DrawerMenuWrapper>
  )
}

const NavMobileList = ({
  content,
  handleClose,
  lastItem,
  // setNestedMenuOpen,
}) => {
  if (content.hideOnDevice === 'mobile') {
    return <Fragment />
  }
  return (
    <Flex
      sx={{
        width: ['100%'],
        flexDirection: 'column',
        alignItems: 'flex-start',
        height: 'auto',
      }}
    >
      {content.header && !content.hideHeader ? (
        <LinkRenderer
          handleClose={handleClose}
          content={content}
          // handleClick={setNestedMenuOpen}
          lastItem={lastItem}
        />
      ) : (
        content.links &&
        content.links.map((l, k) => {
          return (
            <LinkRenderer
              handleClose={handleClose}
              content={l}
              // handleClick={setNestedMenuOpen}
              key={k}
              firstNestedItem={
                getItemIndexByProperty(
                  content.links,
                  '__typename',
                  'ContentfulLinkColumn'
                ) === k
              }
              lastItem={k === content.links.length - 1}
            />
          )
        })
      )}
    </Flex>
  )
}

const LinkRenderer = ({
  content,
  lastItem,
  handleClose,
  handleClick,
  firstNestedItem,
  showNested = false,
  ...rest
}) => {
  const [open, setOpen] = useState(showNested)

  const isLinkHeading = content.links && content.links.length > 0
  const isLinkColumn = content.__typename === 'ContentfulLinkColumn'
  const isPrimaryLink = content.mobileHeaderBold

  const navLinkProps = {
    variant: 'navMobileLinkSmall',
    newRoute: true,
    color: content.color || content.mobileHeaderColor,
    sx: {
      pl: '15px',
      fontSize: isLinkColumn && !showNested && 2,
      pt: isLinkColumn
        ? !isPrimaryLink && !isLinkHeading
          ? '8px'
          : '25px'
        : '8px',
      pb: isLinkColumn
        ? !showNested && (isPrimaryLink || isLinkHeading)
          ? '25px'
          : '12px'
        : '8px',
      mb: isPrimaryLink && !isLinkHeading ? '20px' : '0px',
      borderBottom: isLinkColumn && isPrimaryLink && !showNested && 'default',

      fontWeight: isLinkHeading
        ? 'semiBold'
        : content.mobileHeaderBold || content.bold
        ? 'semiBold'
        : null,
    },
  }

  if (content.toSlug === 'ChatWidget' || content.navLinkSlug === 'ChatWidget') {
    return (
      <GorgiasButton
        render={({ setShowGorgias }) => (
          <Link
            {...navLinkProps}
            onClick={() => {
              setShowGorgias()
            }}
          >
            {content.header || content.navLinkText}
          </Link>
        )}
      />
    )
  }

  return false ? (
    <ImageLink
      {...rest}
      content={content}
      onClick={() => {
        if (!isLinkHeading && (content.toSlug || content.navLinkSlug)) {
          navigate(`/${content.toSlug || content.navLinkSlug}`)
          handleClose()
        } else {
          handleClick(content)
        }
      }}
    />
  ) : (
    <Box
      {...rest}
      sx={{
        pb: showNested && lastItem && 10,
        width: '100%',
        borderBottom: isLinkHeading && isLinkColumn && !showNested && 'default',
      }}
    >
      <Link
        {...navLinkProps}
        to={
          content.navLinkSlug ||
          (isLinkColumn && !isLinkHeading && content.toSlug)
        }
        onClick={() => {
          if (isLinkColumn && isLinkHeading) {
            !showNested && setOpen(!open)
          } else {
            handleClose()
          }
        }}
      >
        {content.header || content.navLinkText}{' '}
        {content.highlightText && (
          <Box
            sx={{
              display: 'inline-block',
              fontWeight: 'semiBold',

              color: content.highlightTextColor,
            }}
          >
            {' '}
            {content.highlightText}
          </Box>
        )}
        {!showNested && isLinkColumn && isLinkHeading ? (
          open ? (
            <Logo
              sx={{
                display: 'inline-block',
                float: 'right',
                width: '12px',
                height: '12px',
                marginTop: '4px',
                marginRight: '10px',
              }}
              src={Minus}
            />
          ) : (
            <Logo
              sx={{
                display: 'inline-block',
                float: 'right',
                width: '12px',
                height: '12px',
                marginTop: '4px',
                marginRight: '10px',
              }}
              src={Plus}
            />
          )
        ) : (
          <Fragment />
        )}
      </Link>
      {open &&
        content.links &&
        content.links.map((l, k) => {
          return (
            <LinkRenderer
              handleClose={handleClose}
              content={l}
              key={k}
              showNested={true}
              firstNestedItem={
                getItemIndexByProperty(
                  content.links,
                  '__typename',
                  'ContentfulLinkColumn'
                ) === k
              }
              lastItem={k === content.links.length - 1}
            />
          )
        })}
    </Box>
  )
}

const GET_SUBNAV_ALERT = graphql`
  query contentfulSubNavAlert {
    contentfulPromotionalAlert(live: { eq: "true" }, subNav: { eq: true }) {
      textSlides
      color
      textColor
      countdownTimer {
        text
        endText
        endTime
        color
        textColor
        timelineEvents {
          text
          color
          textColor
          minutesLeft
        }
      }
    }
  }
`

export default Nav
